import React, { useEffect, useRef, useState } from 'react';
import { DateRangeType, Table } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import {
  formatMemberStatementCellValue,
  getFirstDayOfMonth,
  getFirstDayOfYear,
  getLastDayOfMonth,
  getLastDayOfYear,
  handleApiError
} from 'utils/functions';
import moment from 'moment';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import StatementSummary from './statement-summary';
import PaymentTableRow from './table-row';
import { MemberMonthlyStatementType, MemberStatementType } from 'models/statements.model';
import { MemberTransactionSummaryType } from 'models/transactions.model';

const tableHeaders = ['Month', 'Total Allocated', 'Total Spent', ''];

type Props = {
  filter: string;
};

const MembersTable = ({ filter }: Props) => {
  const { activeCompany } = useDashboardContext();
  const pdfSummaryRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<MemberMonthlyStatementType[]>([]);
  const [loading, setLoading] = useState(false);
  const [pdfSummary, setPdfSummary] = useState<{
    dateRange: DateRangeType['date'];
    summary: MemberTransactionSummaryType;
  } | null>(null);

  const loadMonthlyStatement = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`admin/monthly-member-statement`, {
        params: {
          company_id: activeCompany.id,
          start_date: getFirstDayOfYear(filter),
          end_date: getLastDayOfYear(filter)
        }
      });
      setData(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPdfSummary = async (month: string) => {
    try {
      const startDate = moment(`${filter} ${month}`, 'YYYY MMMM');
      const params = {
        company_id: activeCompany.id,
        start_date: startDate.format(),
        end_date: startDate.endOf('month').format()
      };
      const res = await apiService.get('admin/monthly-member-stat', { params });
      const summary = res.data?.data;
      setPdfSummary?.({
        dateRange: { startDate: new Date(params.start_date), endDate: new Date(params.end_date) },
        summary
      });
    } catch (error) {
      console.log(error);
    }
  };

  const downloadStatement = async (month: string, format: string) => {
    try {
      await loadPdfSummary?.(month);
      const res = await apiService.get<{ data: { results: MemberStatementType[] } }>(
        `admin/monthly-member-report`,
        {
          params: {
            company_id: activeCompany.id,
            start_date: getFirstDayOfMonth(month, filter),
            end_date: getLastDayOfMonth(month, filter)
          }
        }
      );
      const data = res?.data?.data?.results?.map((data, index: number) => ({
        sn: index + 1,
        full_name: `${data.first_name} ${data.last_name}`,
        ...data
      }));
      if (format === 'xlsx') downloadXlsx(data, month);
      if (format === 'pdf') downloadPdf(data, month);
    } catch (err) {
      handleApiError(err);
    } finally {
      setPdfSummary(null);
    }
  };

  const downloadXlsx = (data: MemberStatementType[], month: string) => {
    const filename = `${month}${filter}MemberStatement.xlsx`;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, filename);
  };

  const downloadPdf = async (data: MemberStatementType[], month: string) => {
    const doc = new jsPDF({ unit: 'px' });
    if (pdfSummaryRef.current) {
      const canvas = await html2canvas(pdfSummaryRef.current, { scale: 10 });
      const imageData = canvas.toDataURL('image/jpeg');
      doc.addImage({ imageData, x: 0, y: 0, width: 446, height: 240, format: 'JPEG' });
    }
    const columns = [
      { header: 'S/N', dataKey: 'sn' },
      { header: 'Name of Member', dataKey: 'full_name' },
      { header: 'Department', dataKey: 'department' },
      { header: 'Amount Gifted', dataKey: 'total_gift' },
      { header: 'Amount Allocated', dataKey: 'total_allocation' },
      { header: 'Amount Spent', dataKey: 'total_spent' }
    ];
    autoTable(doc, {
      head: [columns.map((col) => col.header)],
      body: data.map((row) => columns.map((col) => formatMemberStatementCellValue(col, row))),
      styles: { fontSize: 10 },
      headStyles: { fillColor: '#191919' },
      margin: { left: 16, right: 16 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 80 },
        2: { cellWidth: 80 },
        3: { cellWidth: 70 },
        4: { cellWidth: 90 },
        5: { cellWidth: 70 }
      },
      startY: 240
    });
    doc.save(`${month}${filter}MemberStatement.pdf`);
  };

  useEffect(() => {
    if (!activeCompany.id) return;
    loadMonthlyStatement();
  }, []);

  return (
    <>
      {!loading && data.length === 0 ? (
        <div className='mt-8 flex items-center'>
          <p className='mt-1'>Company has no transactions for {filter}</p>
        </div>
      ) : (
        <Table headers={tableHeaders} loading={loading}>
          {data?.map((statement: MemberMonthlyStatementType, index: number) => (
            <PaymentTableRow
              key={index}
              statement={statement}
              handleDownload={downloadStatement}
              filter={filter}
            />
          ))}
        </Table>
      )}
      {pdfSummary ? (
        <StatementSummary
          elementRef={pdfSummaryRef}
          summary={pdfSummary.summary}
          dateRange={pdfSummary.dateRange}
        />
      ) : null}
    </>
  );
};

export default MembersTable;
